import { Dispatch, ReactNode, SetStateAction } from "react";
import { Button, cn } from "@tellescope/design-system";
import { ArrowLeft, LayoutDashboard, Maximize2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { ContactDetailsBlockType } from "../../../types";
import { BlockTitle, BlockTitleProps } from "./BlockTitle";
import { BlockActions } from "./BlockActions";
import { useBlockSortable } from "./useBlockSortable";

export interface BlockHeaderProps<T extends ContactDetailsBlockType> extends BlockTitleProps<T> {
  editMode: boolean;
  setEditMode?: Dispatch<SetStateAction<boolean>>;
  duplicateBlock: () => void;
  deleteBlock: () => void;
  canCreateBlock: boolean;
  canDeleteBlock: boolean;
  onMaximize?: () => void;
  alwaysShowButtons?: boolean;
  actions?: ReactNode;
}

export const BlockHeader = <T extends ContactDetailsBlockType>({
  editMode,
  setEditMode,
  duplicateBlock,
  deleteBlock,
  canCreateBlock,
  canDeleteBlock,
  onMaximize,
  alwaysShowButtons = false,
  actions,
  ...titleProps
}: BlockHeaderProps<T>) => {
  const { t } = useTranslation("contacts");
  const sortable = useBlockSortable();

  return (
    <span
      {...sortable?.listeners}
      className={cn("flex items-center justify-between w-full", sortable && "cursor-grab active:cursor-grabbing")}
    >
      <span className="flex items-center gap-0 flex-1 min-w-0">
        {!!setEditMode && editMode && (
          <Button variant="ghost" size="sm" onClick={() => setEditMode(false)}>
            <ArrowLeft />
            <LayoutDashboard />
          </Button>
        )}
        <BlockTitle {...titleProps} />
      </span>
      <span
        className={cn(
          "flex items-center justify-end",
          !(alwaysShowButtons || editMode) &&
            "invisible group-hover:visible has-[[data-state=open]]:visible has-[[data-block-action-pinned]]:visible"
        )}
      >
        {!!onMaximize && (
          <Button
            iconOnly
            variant="ghost"
            size="sm"
            color="muted"
            hoverLabel={t("Open in full page view")}
            onClick={onMaximize}
          >
            <Maximize2 />
          </Button>
        )}
        {actions}
        {!editMode && (
          <BlockActions
            setEditMode={setEditMode}
            duplicateBlock={duplicateBlock}
            deleteBlock={deleteBlock}
            canCreateBlock={canCreateBlock}
            canDeleteBlock={canDeleteBlock}
          />
        )}
      </span>
    </span>
  );
};
